import { ethers, FetchRequest } from 'ethers';
import axios from 'axios';
import { NftServices, CATEGORIES, ItemFilter, } from './normalize';
import { shortenAddress } from '../../utils/common';
const MINIMUM_VERSION = 1;
function thirdwebClientIdFromRpc(rpc) {
    try {
        const url = new URL(rpc);
        if (!url.hostname.endsWith('rpc.thirdweb.com')) {
            return '';
        }
        return url.pathname.replace(/^\/+|\/+$/g, '');
    }
    catch {
        return '';
    }
}
/** Browser client IDs are domain-locked; Node needs x-secret-key or a public RPC. */
export function createJsonRpcProvider(rpc) {
    const clientId = thirdwebClientIdFromRpc(rpc) ||
        process.env.THIRDWEB_CLIENT_ID ||
        process.env.REACT_APP_THIRDWEB_CLIENT_ID ||
        '';
    const secret = process.env.THIRDWEB_SECRET_KEY || '';
    if (!clientId && !secret) {
        return new ethers.JsonRpcProvider(rpc);
    }
    const req = new FetchRequest(rpc);
    if (clientId) {
        req.setHeader('x-client-id', clientId);
    }
    if (secret) {
        req.setHeader('x-secret-key', secret);
    }
    return new ethers.JsonRpcProvider(req);
}
const DEFAULT_DEADLINE = 300; //seconds
const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';
export class ContractNotDeployedError extends Error {
    constructor(chain, contractType, address, invalidAddress = false) {
        const shortAddress = shortenAddress(address);
        const message = invalidAddress
            ? `${shortAddress} is invalid contract address`
            : `Contract isn't deployed at ${shortAddress} (${contractType} on ${chain})`;
        super(message);
        this.name = 'ContractNotDeployedError';
        this.chain = chain;
        this.contractType = contractType;
        this.address = address;
        this.invalidAddress = invalidAddress;
    }
}
export function isContractNotDeployedError(error) {
    return error instanceof ContractNotDeployedError;
}
function isZeroAddress(address) {
    return !address || address.toLowerCase() === ZERO_ADDRESS;
}
async function readContractVersion(contract, contractType, address, chain) {
    if (isZeroAddress(address)) {
        throw new ContractNotDeployedError(chain, contractType, address, true);
    }
    if (!contractHasFn(contract, 'VERSION')) {
        return MINIMUM_VERSION;
    }
    try {
        const version = await contract.VERSION();
        return Number(version);
    }
    catch {
        throw new ContractNotDeployedError(chain, contractType, address);
    }
}
function cachedNftHasMetadata(cached) {
    if (!cached)
        return false;
    return (Array.isArray(cached.attributes) ||
        Array.isArray(cached.categories) ||
        typeof cached.category === 'string' ||
        typeof cached.image === 'string');
}
function contractHasFn(contract, name) {
    try {
        contract.getFunction(name);
        return true;
    }
    catch {
        return false;
    }
}
async function readMarketProceeds(market, address) {
    try {
        if (contractHasFn(market, 'pendingWithdrawals')) {
            return await market.pendingWithdrawals(address);
        }
        if (contractHasFn(market, 'getProceedsByOwner')) {
            return await market.getProceedsByOwner(address);
        }
        if (contractHasFn(market, 'getMyProceeds')) {
            return await market.getMyProceeds();
        }
    }
    catch {
        return BigInt(0);
    }
    return BigInt(0);
}
async function withdrawMarketProceeds(market) {
    if (contractHasFn(market, 'withdraw')) {
        return market.withdraw().catch(() => null);
    }
    if (contractHasFn(market, 'withdrawProceeds')) {
        return market.withdrawProceeds().catch(() => null);
    }
    return null;
}
let contracts = {};
let providers = {};
function requireContracts(chain, role) {
    const chainContracts = contracts[chain];
    if (!chainContracts?.length) {
        throw new Error(`${role} contract not initialized for chain: ${chain}`);
    }
    return chainContracts;
}
function requireLatestContract(chain, role) {
    const chainContracts = requireContracts(chain, role);
    return chainContracts[chainContracts.length - 1];
}
function requireContractVersion(chain, version, role) {
    const chainContracts = contracts[chain];
    if (!chainContracts?.length) {
        throw new Error(`${role} contract not initialized for chain: ${chain}`);
    }
    if (chainContracts.length <= version) {
        throw new Error(`${role} contract not initialized for chain: ${chain} version: ${version}`);
    }
    return chainContracts[version];
}
/** True when initInstance registered at least one versioned NFT + market pair for the chain. */
export function isEvmChainInitialized(chain) {
    const chainContracts = contracts[chain];
    return (!!chainContracts?.length &&
        chainContracts.every(({ nft_version, market_version }) => nft_version != null && market_version != null));
}
export const ethToWei = (eth) => {
    if (typeof eth === 'number') {
        eth = eth.toString();
    }
    return ethers.parseEther(eth);
};
export const weiToEther = (wei) => {
    return Number(ethers.formatUnits(wei, 'ether'));
};
export const getChainIdFromRpcUrl = async (rpc) => {
    const response = await axios.post(rpc, {
        id: 1,
        jsonrpc: '2.0',
        method: 'eth_chainId',
        params: [],
    });
    return response.data.result;
};
const buildContractInfo = async (chain, nft_address, market_address, nft_contract, market_contract, index) => {
    const nft_version = await readContractVersion(nft_contract, 'NFT', nft_address, chain);
    const market_version = await readContractVersion(market_contract, 'Marketplace', market_address, chain);
    return {
        nft: nft_contract,
        market: market_contract,
        version: index,
        nft_version,
        market_version,
    };
};
export const initInstance = async (chain, contract_addresses, rpc, wallet_address) => {
    const rpcProvider = createJsonRpcProvider(rpc);
    const provider = wallet_address
        ? new ethers.JsonRpcSigner(rpcProvider, wallet_address)
        : rpcProvider;
    providers[chain] = {
        rpcProvider,
        provider,
        address: wallet_address,
    };
    contracts[chain] = [];
    for await (const { nft_address, nft_abi, market_address, market_abi } of contract_addresses) {
        if (isZeroAddress(market_address)) {
            throw new ContractNotDeployedError(chain, 'Marketplace', market_address, true);
        }
        const market_contract = new ethers.Contract(market_address, market_abi, provider);
        // The marketplace stores its NFT contract as an immutable, so we only need the
        // marketplace address in config and can resolve the NFT address on-chain.
        const resolved_nft_address = String(await market_contract.nftContract());
        if (nft_address) {
            if (nft_address !== resolved_nft_address) {
                console.warn(`NFT address mismatch: ${nft_address} !== ${resolved_nft_address}`);
            }
        }
        else {
            console.log(`Using resolved NFT address: ${resolved_nft_address}`);
        }
        const nft_contract = new ethers.Contract(nft_address, nft_abi, provider);
        contracts[chain].push(await buildContractInfo(chain, nft_address, market_address, nft_contract, market_contract, contracts[chain].length));
    }
};
export const setContracts = async (chain, nft_contract, market_contract) => {
    console.log('Setting contracts...');
    contracts[chain] = [
        await buildContractInfo(chain, String(nft_contract.target), String(market_contract.target), nft_contract, market_contract, 0),
    ];
};
export const setWallet = (chain, wallet) => {
    contracts[chain] = contracts[chain].map((contract) => ({
        ...contract,
        nft: contract.nft.connect(wallet),
        market: contract.market.connect(wallet),
    }));
};
export const setReadOnlyProvider = (chain) => {
    const provider = providers[chain]?.provider;
    if (contracts[chain] && provider instanceof ethers.JsonRpcSigner) {
        setWallet(chain, provider);
    }
};
export const transferOwnership = async (chain, newOwner) => {
    const { nft, market } = requireLatestContract(chain, 'NFT');
    let tx = await nft.transferOwnership(newOwner);
    await tx.wait();
    tx = await market.transferOwnership(newOwner);
    await tx.wait();
};
export const acceptOwnership = async (chain) => {
    const { nft, market } = requireLatestContract(chain, 'NFT');
    let tx = await nft.acceptOwnership();
    await tx.wait();
    tx = await market.acceptOwnership();
    await tx.wait();
};
export const getOwners = async (chain) => {
    const { nft, market } = requireLatestContract(chain, 'NFT');
    let nft_owner = await nft.owner();
    let market_owner = await market.owner();
    return {
        nft: nft_owner.toString(),
        market: market_owner.toString(),
    };
};
export const getPendingOwners = async (chain) => {
    const { nft, market } = requireLatestContract(chain, 'NFT');
    const [nftPending, marketPending] = await Promise.all([
        nft.pendingOwner(),
        market.pendingOwner(),
    ]);
    return {
        nft: nftPending?.toString?.() ?? String(nftPending),
        market: marketPending?.toString?.() ?? String(marketPending),
    };
};
export const getScheduledPriceChange = async (chain) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    return await nft.scheduledMintPrice();
};
export const getPriceChangeTimestamp = async (chain) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    return await nft.priceChangeTimestamp();
};
/** On-chain timelock duration (seconds) before a scheduled mint price can be applied. */
export const getPriceTimelock = async (chain) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    return await nft.PRICE_TIMELOCK();
};
export const scheduleMintPriceChange = async (chain, price) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    const tx = await nft.scheduleMintPriceChange(price);
    return await tx.wait();
};
export const applyMintPriceChange = async (chain, price) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    const tx = await nft.applyMintPriceChange();
    return await tx.wait();
};
export const cancelMintPriceChange = async (chain, price) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    const tx = await nft.cancelMintPriceChange();
    return await tx.wait();
};
export const tokenURI = async (chain, tokenId) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    return await nft.tokenURI(tokenId);
};
export const getMintPrice = async (chain) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    return await nft.mintPrice();
};
/** Real Irys gateway URI length stand-in for pre-upload gas estimates. */
export const SAMPLE_IRYS_METADATA_URI = 'https://gateway.irys.xyz/MrWwmbGdK91bmLjIDRRNycTZUdAkgOiw7O5hphpWQi8';
function isInsufficientFundsError(error) {
    const err = error;
    if (err?.code === 'INSUFFICIENT_FUNDS' || err?.code === -32000)
        return true;
    const text = `${err?.shortMessage ?? ''} ${err?.message ?? ''} ${err?.info?.error?.message ?? ''}`.toLowerCase();
    return text.includes('insufficient funds');
}
function networkFeeErrorMessage(error) {
    if (isInsufficientFundsError(error))
        return 'Insufficient funds';
    const err = error;
    if (typeof err?.shortMessage === 'string' && err.shortMessage.trim())
        return err.shortMessage;
    if (error instanceof Error && error.message.trim())
        return error.message;
    return 'Fee estimate unavailable';
}
function toListingInfos(infos) {
    return infos.map((info) => Array.isArray(info)
        ? { tokenId: info[0], price: info[1] }
        : { tokenId: info.tokenId, price: info.price });
}
const GWEI = 1000000000n;
/** Pad estimateGas like wallets (~MetaMask Market). */
const GAS_LIMIT_BUFFER_BPS = 15000n; // ×1.5
/**
 * MetaMask fee-history floors / base-fee padding (Ethereum mainnet style).
 * Without these, raw eth_feeHistory tips can be dust (~0.01–0.1 gwei) and the
 * site under-quotes network fee vs what MetaMask Market shows.
 */
const FEE_SPEED_SETTINGS = {
    low: { label: 'Low', delay: '24–48 sec', baseFeeBps: 11000n, minPriorityFee: GWEI },
    market: {
        label: 'Market',
        delay: '12–24 sec',
        baseFeeBps: 12000n,
        minPriorityFee: (GWEI * 3n) / 2n, // 1.5 gwei
    },
    fast: { label: 'Fast', delay: '<12 sec', baseFeeBps: 12500n, minPriorityFee: 2n * GWEI },
};
function medianBigInt(values) {
    if (!values.length)
        return 0n;
    const sorted = [...values].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2n;
}
function withGasLimitBuffer(gasLimit) {
    return (gasLimit * GAS_LIMIT_BUFFER_BPS) / 10000n;
}
function maxBigInt(a, b) {
    return a > b ? a : b;
}
function buildFeeOption(speed, baseFee, priorityFee) {
    const settings = FEE_SPEED_SETTINGS[speed];
    const maxPriorityFeePerGas = maxBigInt(priorityFee, settings.minPriorityFee);
    // MetaMask pads base fee for the max-fee ceiling, then adds the tip.
    const paddedBaseFee = (baseFee * settings.baseFeeBps) / 10000n;
    const maxFeePerGas = paddedBaseFee + maxPriorityFeePerGas;
    // Likely cost still uses the live base fee + tip (not the padded ceiling).
    const estimatedFeePerGas = baseFee + maxPriorityFeePerGas;
    return {
        speed,
        label: settings.label,
        delay: settings.delay,
        maxFeePerGas,
        maxPriorityFeePerGas,
        estimatedFeePerGas,
    };
}
/**
 * MetaMask-style EIP-1559 fee options from eth_feeHistory.
 * Uses next-block base fee, recent priority-fee percentiles, and priority floors.
 */
export async function getEip1559FeeOptions(chain) {
    // Hardhat / local network has no real fee market — skip eth_feeHistory.
    if (chain === 'Localhost') {
        return [
            buildFeeOption('low', GWEI, GWEI),
            buildFeeOption('market', GWEI, GWEI),
            buildFeeOption('fast', GWEI, GWEI),
        ];
    }
    const rpc = providers[chain]?.rpcProvider;
    if (!rpc) {
        throw new Error(`RPC not initialized for ${chain}`);
    }
    let baseFee = 0n;
    let lowPriorityFee = 0n;
    let marketPriorityFee = 0n;
    let fastPriorityFee = 0n;
    try {
        // Percentiles aligned with MetaMask fee-history low/medium/high bands.
        const history = await rpc.send('eth_feeHistory', [
            '0xa', // 10 blocks
            'latest',
            [10, 20, 30],
        ]);
        const baseFees = history?.baseFeePerGas ?? [];
        // feeHistory returns N+1 base fees; last is the next block's base fee.
        if (baseFees.length) {
            baseFee = BigInt(baseFees[baseFees.length - 1]);
        }
        const rewards = history?.reward ?? [];
        const tipsFor = (index) => rewards
            .map((row) => (row?.[index] != null ? BigInt(row[index]) : null))
            .filter((v) => v != null && v > 0n);
        lowPriorityFee = medianBigInt(tipsFor(0));
        marketPriorityFee = medianBigInt(tipsFor(1));
        fastPriorityFee = medianBigInt(tipsFor(2));
    }
    catch {
        // Fall through to getFeeData.
    }
    if (baseFee === 0n || marketPriorityFee === 0n) {
        const latest = await rpc.getBlock('latest');
        if (baseFee === 0n)
            baseFee = latest?.baseFeePerGas ?? 0n;
        const feeData = await rpc.getFeeData();
        if (marketPriorityFee === 0n) {
            marketPriorityFee = feeData.maxPriorityFeePerGas ?? 0n;
        }
        // Legacy (non-EIP-1559) networks: use gasPrice for everything.
        if (baseFee === 0n && feeData.gasPrice && feeData.gasPrice > 0n) {
            return [
                {
                    speed: 'low',
                    label: 'Low',
                    delay: '24–48 sec',
                    maxFeePerGas: feeData.gasPrice,
                    maxPriorityFeePerGas: feeData.gasPrice,
                    estimatedFeePerGas: feeData.gasPrice,
                },
                {
                    speed: 'market',
                    label: 'Market',
                    delay: '12–24 sec',
                    maxFeePerGas: feeData.gasPrice,
                    maxPriorityFeePerGas: feeData.gasPrice,
                    estimatedFeePerGas: feeData.gasPrice,
                },
                {
                    speed: 'fast',
                    label: 'Fast',
                    delay: '<12 sec',
                    maxFeePerGas: feeData.gasPrice,
                    maxPriorityFeePerGas: feeData.gasPrice,
                    estimatedFeePerGas: feeData.gasPrice,
                },
            ];
        }
    }
    if (marketPriorityFee === 0n)
        marketPriorityFee = FEE_SPEED_SETTINGS.market.minPriorityFee;
    if (lowPriorityFee === 0n)
        lowPriorityFee = FEE_SPEED_SETTINGS.low.minPriorityFee;
    if (fastPriorityFee === 0n || fastPriorityFee <= marketPriorityFee) {
        fastPriorityFee = maxBigInt((marketPriorityFee * 3n) / 2n, FEE_SPEED_SETTINGS.fast.minPriorityFee);
    }
    return [
        buildFeeOption('low', baseFee, lowPriorityFee),
        buildFeeOption('market', baseFee, marketPriorityFee),
        buildFeeOption('fast', baseFee, fastPriorityFee),
    ];
}
export async function getMarketEip1559Fees(chain) {
    const options = await getEip1559FeeOptions(chain);
    const market = options.find((option) => option.speed === 'market') ?? options[0];
    return {
        maxFeePerGas: market.maxFeePerGas,
        maxPriorityFeePerGas: market.maxPriorityFeePerGas,
        estimatedFeePerGas: market.estimatedFeePerGas,
    };
}
async function gasLimitToNetworkFeeEth(chain, gasLimit) {
    try {
        const feeOptions = await getEip1559FeeOptions(chain);
        const fees = feeOptions.find((option) => option.speed === 'market') ?? feeOptions[0];
        const bufferedGasLimit = withGasLimitBuffer(gasLimit);
        return {
            ok: true,
            // MetaMask-style likely cost: buffered gas × (base + tip), not maxFee ceiling.
            networkFeeEth: weiToEther(bufferedGasLimit * fees.estimatedFeePerGas),
            gasLimit: bufferedGasLimit,
            maxFeePerGas: fees.maxFeePerGas,
            maxPriorityFeePerGas: fees.maxPriorityFeePerGas,
            feeOptions: feeOptions.map((option) => ({
                speed: option.speed,
                label: option.label,
                delay: option.delay,
                networkFeeEth: weiToEther(bufferedGasLimit * option.estimatedFeePerGas),
            })),
        };
    }
    catch (error) {
        return { ok: false, message: networkFeeErrorMessage(error) };
    }
}
/**
 * Estimate network gas for mint / list / delist / updatePrice / buy.
 * Action-specific args are selected via the discriminated `params.action` union.
 */
export async function estimateNetworkFees(chain, params) {
    if (!params.from) {
        return { ok: false, message: 'Wallet address required for gas estimate' };
    }
    try {
        switch (params.action) {
            case 'mint': {
                if (!params.to) {
                    return { ok: false, message: 'Recipient address required for mint estimate' };
                }
                if (params.count < 1) {
                    return { ok: false, message: 'Mint count must be >= 1' };
                }
                const { nft } = requireLatestContract(chain, 'NFT');
                const royaltyBps = params.royaltyBps ?? 500;
                const mintValueWei = BigInt(await getMintPrice(chain)) * BigInt(params.count);
                const uris = Array.from({ length: params.count }, () => SAMPLE_IRYS_METADATA_URI);
                const gasLimit = await nft.batchMint.estimateGas(params.to, uris, royaltyBps, {
                    value: mintValueWei,
                    from: params.from,
                });
                return gasLimitToNetworkFeeEth(chain, gasLimit);
            }
            case 'list': {
                if (!params.infos?.length) {
                    return { ok: false, message: 'List infos required for gas estimate' };
                }
                const { market } = requireContractVersion(chain, params.version, 'Market');
                const gasLimit = await market.batchListing.estimateGas(toListingInfos(params.infos), {
                    from: params.from,
                });
                return gasLimitToNetworkFeeEth(chain, gasLimit);
            }
            case 'delist': {
                if (!params.tokenIds?.length) {
                    return { ok: false, message: 'Token IDs required for delist estimate' };
                }
                const { market } = requireContractVersion(chain, params.version, 'Market');
                const gasLimit = await market.batchCancelListing.estimateGas(params.tokenIds, {
                    from: params.from,
                });
                return gasLimitToNetworkFeeEth(chain, gasLimit);
            }
            case 'updatePrice': {
                if (!params.infos?.length) {
                    return { ok: false, message: 'Price infos required for gas estimate' };
                }
                const { market } = requireContractVersion(chain, params.version, 'Market');
                const gasLimit = await market.batchUpdatePrice.estimateGas(toListingInfos(params.infos), {
                    from: params.from,
                });
                return gasLimitToNetworkFeeEth(chain, gasLimit);
            }
            case 'buy': {
                if (!params.tokenIds?.length) {
                    return { ok: false, message: 'Token IDs required for buy estimate' };
                }
                const { market } = requireContractVersion(chain, params.version, 'Market');
                const totalPrice = params.totalPrice ?? (await getTotalPrice(chain, params.version, params.tokenIds));
                const deadline = params.deadline ?? Math.floor(Date.now() / 1000) + DEFAULT_DEADLINE;
                const maxPrice = params.maxPrice ?? maxPriceWithSlippage(totalPrice);
                const gasLimit = await market.batchFulfillListing.estimateGas(params.tokenIds, maxPrice, deadline, { value: totalPrice, from: params.from });
                return gasLimitToNetworkFeeEth(chain, gasLimit);
            }
            default: {
                const _exhaustive = params;
                return {
                    ok: false,
                    message: `Unsupported fee estimate action: ${_exhaustive.action}`,
                };
            }
        }
    }
    catch (error) {
        return { ok: false, message: networkFeeErrorMessage(error) };
    }
}
export const getDefaultBatchSize = async (chain) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    return await nft.defaultBatchSize();
};
export const setDefaultBatchSize = async (chain, count) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    const tx = await nft.setDefaultBatchSize(count);
    return await tx.wait();
};
export const getWhitelistBatchSize = async (chain) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    return await nft.whitelistBatchSize();
};
export const setWhitelistBatchSize = async (chain, size) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    const tx = await nft.setWhitelistBatchSize(size);
    return await tx.wait();
};
export const isWhitelisted = async (chain, account) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    return await nft.whitelisted(account);
};
export const setWhitelisted = async (chain, account, allowed) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    const tx = await nft.setWhitelisted(account, allowed);
    return await tx.wait();
};
export const setWhitelistedBatch = async (chain, accounts, allowed) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    const tx = await nft.setWhitelistedBatch(accounts, allowed);
    return await tx.wait();
};
export const removeWhitelisted = async (chain, account) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    const tx = await nft.removeWhitelisted(account);
    return await tx.wait();
};
export const removeWhitelistedBatch = async (chain, accounts) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    const tx = await nft.removeWhitelistedBatch(accounts);
    return await tx.wait();
};
export const getAllowedBatchSize = async (chain, account) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    return await nft.allowedBatchSize(account);
};
export const getMaxRoyaltyBps = async (chain) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    return await nft.maxRoyaltyBps();
};
export const setMaxRoyaltyBps = async (chain, bps) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    const tx = await nft.setMaxRoyaltyBps(bps);
    return await tx.wait();
};
export const pauseMinting = async (chain) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    const tx = await nft.pauseMinting();
    return await tx.wait();
};
export const unpauseMinting = async (chain) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    const tx = await nft.unpauseMinting();
    return await tx.wait();
};
export const isMintingPaused = async (chain) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    return await nft.mintingPaused();
};
export const getAccruedMintFees = async (chain) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    return await nft.accruedMintFees();
};
export const getAccruedMarketFees = async (chain) => {
    const { market } = requireLatestContract(chain, 'Market');
    return await market.accruedMarketFees();
};
export const withdrawNFTFee = async (chain) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    const tx = await nft.withdrawFee();
    return [await tx.wait()];
};
export const rescueNftEth = async (chain, to) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    const tx = await nft.rescueEth(to);
    return await tx.wait();
};
export const openMarket = async (chain) => {
    const { market } = requireLatestContract(chain, 'Market');
    const tx = await market.openMarket();
    return await tx.wait();
};
export const isMarketOpened = async (chain) => {
    const { market } = requireLatestContract(chain, 'Market');
    return await market.isMarketOpen();
};
export const closeMarket = async (chain) => {
    const { market } = requireLatestContract(chain, 'Market');
    const tx = await market.closeMarket();
    return await tx.wait();
};
export const allowEmergencyDelisting = async (chain) => {
    const { market } = requireLatestContract(chain, 'Market');
    const tx = await market.allowEmergencyDelisting();
    return await tx.wait();
};
export const disableEmergencyDelisting = async (chain) => {
    const { market } = requireLatestContract(chain, 'Market');
    const tx = await market.disableEmergencyDelisting();
    return await tx.wait();
};
export const rescueERC721 = async (chain, token, tokenId, to) => {
    const { market } = requireLatestContract(chain, 'Market');
    const tx = await market.rescueERC721(token, tokenId, to);
    return await tx.wait();
};
/** @deprecated Use {rescueERC721} with the CVS NFT contract address */
export const rescueNft = async (chain, tokenId, to) => {
    const { market } = requireLatestContract(chain, 'Market');
    const token = await market.nftContract();
    return rescueERC721(chain, token, tokenId, to);
};
export const rescueEth = async (chain, to) => {
    const { market } = requireLatestContract(chain, 'Market');
    const tx = await market.rescueEth(to);
    return await tx.wait();
};
export const isEmergencyDelistingAllowed = async (chain) => {
    const { market } = requireLatestContract(chain, 'Market');
    return await market.isEmergencyDelistingEnabled();
};
export const setMarketFee = async (chain, fee) => {
    const { market } = requireLatestContract(chain, 'Market');
    const tx = await market.setMarketFee(fee);
    return await tx.wait();
};
export const getMarketFee = async (chain) => {
    const { market } = requireLatestContract(chain, 'Market');
    return await market.marketFeePercent();
};
export const getMarketNftContract = async (chain) => {
    const { market } = requireLatestContract(chain, 'Market');
    return await market.nftContract();
};
export const freezeListing = async (chain, tokenId) => {
    const { market } = requireLatestContract(chain, 'Market');
    const tx = await market.freezeListing(tokenId);
    return await tx.wait();
};
export const unfreezeListing = async (chain, tokenId) => {
    const { market } = requireLatestContract(chain, 'Market');
    const tx = await market.unfreezeListing(tokenId);
    return await tx.wait();
};
export const getMyProceeds = async (chain) => {
    let total = BigInt(0);
    for (const { market } of requireContracts(chain, 'Market')) {
        const runner = market.runner;
        if (runner && typeof runner === 'object' && 'getAddress' in runner) {
            const address = await runner.getAddress();
            total += await readMarketProceeds(market, address);
        }
    }
    return total;
};
export const withdraw = async (chain) => {
    const tx_arr = [];
    for (const { market } of requireContracts(chain, 'Market')) {
        const tx = await withdrawMarketProceeds(market);
        if (tx)
            tx_arr.push(await tx.wait());
    }
    return tx_arr;
};
/** @deprecated Use {withdraw} */
export const withdrawProceeds = withdraw;
export const withdrawMarketFee = async (chain) => {
    const { market } = requireLatestContract(chain, 'Market');
    const tx = await market.withdrawFee();
    return [await tx.wait()];
};
export const getListingCount = async (chain) => {
    const { market } = requireLatestContract(chain, 'Market');
    return await market.totalListings();
};
export const getListing = async (chain, tokenId) => {
    const { market } = requireLatestContract(chain, 'Market');
    return await market.listings(tokenId);
};
export const getTotalPrice = async (chain, version, tokenIds) => {
    const { market } = requireContractVersion(chain, version, 'Market');
    return await market.getTotalPrice(tokenIds);
};
const fetchProxyItem = (item, version) => {
    return {
        owner: item.owner,
        tokenId: item.tokenId,
        price: item.price,
        version,
    };
};
export const getAllListings = async (chain) => {
    const allListings = [];
    for await (const contract of requireContracts(chain, 'Market')) {
        const { market, version } = contract;
        const listings = await market
            .getAllListings()
            .then((res) => res.map((item) => fetchProxyItem(item, version)))
            .catch(() => []);
        allListings.push(...listings);
    }
    return allListings;
};
export const fetchAllListings = async (cache, chain) => {
    const allListings = await getAllListings(chain);
    const nfts = [];
    for (let i = 0; i < allListings.length; i++) {
        nfts.push(await normalizeNFTData(cache, chain, allListings[i].version, allListings[i].owner, allListings[i].tokenId, allListings[i].price, ItemFilter.Listed));
    }
    return nfts;
};
export const getListingByRange = async (chain, start, length) => {
    const { market } = requireLatestContract(chain, 'Market');
    return await market.getListings(start, length);
};
export const getAllMyListings = async (chain) => {
    const allMyListings = [];
    for await (const contract of requireContracts(chain, 'Market')) {
        const { market, version } = contract;
        const listings = await market
            .getAllMyListings()
            .then((res) => res.map((item) => fetchProxyItem(item, version)))
            .catch(() => []);
        allMyListings.push(...listings);
    }
    return allMyListings;
};
const resolveCategories = (metadata) => {
    const attrs = Array.isArray(metadata.attributes) ? metadata.attributes : [];
    const categoryAttrs = attrs.filter((a) => a?.trait_type === 'Category');
    const categoriesFromAttrs = categoryAttrs
        .map((a) => (typeof a?.value === 'string' ? a.value.trim() : ''))
        .filter(Boolean);
    const categoriesFromMeta = Array.isArray(metadata.categories)
        ? metadata.categories
            .filter((c) => typeof c === 'string' && Boolean(c.trim()))
            .map((c) => c.trim())
        : [];
    return [
        ...new Set(categoriesFromMeta.length
            ? categoriesFromMeta
            : categoriesFromAttrs.length
                ? categoriesFromAttrs
                : typeof metadata.category === 'string' && metadata.category
                    ? [metadata.category]
                    : [CATEGORIES[0] || '']),
    ].filter(Boolean);
};
const toNftInfo = (chain, version, owner, tokenId, price, type, metadata, uri) => ({
    version,
    ...metadata,
    categories: resolveCategories(metadata),
    tokenId: typeof tokenId === 'bigint' ? Number(tokenId) : tokenId,
    uri,
    owner,
    price: weiToEther(price),
    weiPrice: Number(price),
    name: metadata.name ?? '',
    type,
    displayKey: `${chain}-v${version}-#${tokenId}`,
});
const normalizeNFTData = async (cache, chain, version, owner, tokenId, price, type) => {
    const cachedNFT = cache.filter((item) => item.tokenId === tokenId && item.version == version)[0];
    const uri = cachedNFT?.uri ?? (await contracts[chain][version].nft.tokenURI(tokenId));
    // Only reuse cache when it actually holds metadata (attributes/image/categories).
    // Token stubs { tokenId, version, uri, name } must still fetch the URI JSON.
    const metadata = cachedNftHasMetadata(cachedNFT)
        ? cachedNFT
        : await fetchNftUri(uri);
    return toNftInfo(chain, version, owner, tokenId, price, type, metadata, uri);
};
export const fetchAllMyListings = async (cache, chain) => {
    const allMyListings = await getAllMyListings(chain);
    const nfts = [];
    for (let i = 0; i < allMyListings.length; i++) {
        nfts.push(await normalizeNFTData(cache, chain, allMyListings[i].version, allMyListings[i].owner, allMyListings[i].tokenId, allMyListings[i].price, ItemFilter.Listed));
    }
    return nfts;
};
export const fetchNftUri = async (uri) => {
    return await axios
        .get(uri)
        .then((res) => res.data)
        .catch((e) => {
        console.log(e.code);
        return {};
    });
};
/** Enumerate owned token ids via Transfer logs (Hardhat / no Alchemy). */
const getOwnedTokenIdsFromLogs = async (nft, owner) => {
    const received = await nft.queryFilter(nft.filters.Transfer(null, owner));
    const sent = await nft.queryFilter(nft.filters.Transfer(owner, null));
    const owned = new Map();
    for (const log of received) {
        const tokenId = log.args?.tokenId;
        if (tokenId == null)
            continue;
        owned.set(tokenId.toString(), tokenId);
    }
    for (const log of sent) {
        const tokenId = log.args?.tokenId;
        if (tokenId == null)
            continue;
        owned.delete(tokenId.toString());
    }
    return [...owned.values()].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
};
const fetchOwnedTokenRefs = async (chain, address) => {
    if (NftServices?.getOwned) {
        const response = await NftServices.getOwned(chain, address);
        const payload = response?.data;
        const list = Array.isArray(payload)
            ? payload
            : Array.isArray(payload?.data)
                ? payload.data
                : [];
        return list.map((item) => ({
            ...item,
            tokenId: item.tokenId,
            version: Number(item.version),
        }));
    }
    // Hardhat / Node tests: index Transfer events instead of ERC721Enumerable.
    const refs = [];
    for (const contract of contracts[chain] ?? []) {
        const tokenIds = await getOwnedTokenIdsFromLogs(contract.nft, address);
        for (const tokenId of tokenIds) {
            refs.push({ tokenId, version: contract.version, categories: [] });
        }
    }
    return refs;
};
const ownedRefHasMetadata = (ref) => Boolean(ref.name ||
    ref.image ||
    (Array.isArray(ref.attributes) && ref.attributes.length) ||
    (Array.isArray(ref.categories) && ref.categories.length) ||
    typeof ref.category === 'string');
export const getNftsByOwner = async (cache, chain, address) => {
    const nfts = [];
    if (!address)
        return nfts;
    const owned = await fetchOwnedTokenRefs(chain, address);
    const mintPriceByVersion = new Map();
    for (const ref of owned) {
        const version = Number(ref.version);
        const tokenId = typeof ref.tokenId === 'bigint' ? ref.tokenId : BigInt(ref.tokenId);
        const contract = contracts[chain]?.[version];
        if (!contract)
            continue;
        let mintPrice = mintPriceByVersion.get(version);
        if (mintPrice === undefined) {
            mintPrice = BigInt(await contract.nft.mintPrice());
            mintPriceByVersion.set(version, mintPrice);
        }
        if (typeof process !== 'undefined' && process.versions?.node) {
            // Hardhat contract tests only need ownership stubs.
            nfts.push({
                owner: address,
                tokenId,
                price: mintPrice,
                version,
            });
            continue;
        }
        // EVM + Alchemy: metadata already on the owned payload — skip tokenURI / URI JSON fetch.
        if (ownedRefHasMetadata(ref)) {
            const { tokenId: _t, version: _v, contractAddress: _c, uri, ...metadata } = ref;
            nfts.push(toNftInfo(chain, version, address, tokenId, mintPrice, ItemFilter.MyWallet, metadata, typeof uri === 'string' ? uri : ''));
            continue;
        }
        nfts.push(await normalizeNFTData(cache, chain, version, address, tokenId, mintPrice, ItemFilter.MyWallet));
    }
    return nfts;
};
export const getMyNfts = async (cache, chain) => {
    return await getNftsByOwner(cache, chain, contracts[chain][contracts[chain].length - 1].nft.runner?.address);
};
export const getMyListingsByRange = async (chain, start, length) => {
    const { market } = requireLatestContract(chain, 'Market');
    return await market.getMyListings(start, length);
};
export const getMyListingCount = async (chain) => {
    const { market } = requireLatestContract(chain, 'Market');
    return await market.getMyListingsCount();
};
///user action
export const mintNft = async (chain, to, uri, royaltyBps) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    const tx = await nft.payToMint(to, uri, royaltyBps, { value: await getMintPrice(chain) });
    return await tx.wait();
};
export const burnNFT = async (chain, version, tokenId) => {
    const { nft } = requireContractVersion(chain, version, 'NFT');
    const tx = await nft.burn(tokenId);
    return await tx.wait();
};
export const batchBurn = async (chain, version, tokenIds) => {
    const { nft } = requireContractVersion(chain, version, 'NFT');
    const tx = await nft.batchBurn(tokenIds);
    return await tx.wait();
};
export const batchMint = async (chain, to, uris, royaltyBps) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    const value = (await getMintPrice(chain)) * BigInt(uris.length);
    const tx = await nft.batchMint(to, uris, royaltyBps, { value });
    return await tx.wait();
};
export const emergencyDelist = async (chain, tokenIds) => {
    const { market } = requireLatestContract(chain, 'Market');
    const tx = await market.emergencyDelist(tokenIds);
    return await tx.wait();
};
export const listNft = async (chain, tokenId, price) => {
    const { market } = requireLatestContract(chain, 'Market');
    const tx = await market.addListing(tokenId, price);
    return await tx.wait();
};
export const isApprovedMarket = async (chain, address) => {
    const approval = [];
    for await (const contract of requireContracts(chain, 'Market')) {
        approval.push(await contract.nft.isApprovedForAll(address, contract.market.target).catch(() => false));
    }
    return approval;
};
export const getMyProviderAddress = (chain) => {
    if (contracts[chain] && contracts[chain].length) {
        return contracts[chain][contracts[chain].length - 1].nft.runner?.address;
    }
    return undefined;
};
export const isApprovedMyAddress = async (chain) => {
    const address = getMyProviderAddress(chain);
    if (address) {
        return await isApprovedMarket(chain, address);
    }
    return [];
};
export const approveMarketForNFT = async (chain, version) => {
    const { nft, market } = requireContractVersion(chain, version, 'NFT');
    const tx = await nft.setApprovalForAll(market.target, true);
    return tx.wait();
};
export const batchList = async (chain, version, infos) => {
    const { market } = requireContractVersion(chain, version, 'Market');
    const tx = await market.batchListing(infos);
    return await tx.wait();
};
export const updatePrice = async (chain, tokenId, price) => {
    const { market } = requireLatestContract(chain, 'Market');
    const tx = await market.updatePrice(tokenId, price);
    return await tx.wait();
};
export const batchUpdatePrice = async (chain, version, infos) => {
    const { market } = requireContractVersion(chain, version, 'Market');
    const tx = await market.batchUpdatePrice(infos);
    return await tx.wait();
};
export const delistNft = async (chain, tokenId) => {
    const { market } = requireLatestContract(chain, 'Market');
    const tx = await market.cancelListing(tokenId);
    return await tx.wait();
};
export const batchDelist = async (chain, version, tokenIds) => {
    const { market } = requireContractVersion(chain, version, 'Market');
    const tx = await market.batchCancelListing(tokenIds);
    return await tx.wait();
};
export const maxPriceWithSlippage = (price) => {
    return (price * BigInt(103)) / BigInt(100); //slippage is +3%
};
export const buyNft = async (chain, tokenId, opts) => {
    const chainContracts = requireContracts(chain, 'Market');
    const { market } = chainContracts[chainContracts.length - 1];
    const price = await getTotalPrice(chain, chainContracts.length - 1, [tokenId]);
    const deadlineT = opts?.deadline
        ? opts?.deadline
        : Math.floor(Date.now() / 1000) + DEFAULT_DEADLINE;
    const maxPriceT = opts?.maxPrice ? opts?.maxPrice : maxPriceWithSlippage(price);
    const tx = await market.fulfillListing(tokenId, maxPriceT, deadlineT, { value: price });
    return await tx.wait();
};
export const batchBuy = async (chain, version, tokenIds, totalPrice, opts) => {
    const { market } = requireContractVersion(chain, version, 'Market');
    const deadlineT = opts?.deadline
        ? opts?.deadline
        : Math.floor(Date.now() / 1000) + DEFAULT_DEADLINE;
    const maxPriceT = opts?.maxPrice ? opts?.maxPrice : maxPriceWithSlippage(totalPrice);
    const tx = await market.batchFulfillListing(tokenIds, maxPriceT, deadlineT, {
        value: totalPrice,
    });
    return await tx.wait();
};
export const getNFTBalance = async (chain, address) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    return await nft.balanceOf(address);
};
export const getNFTOwner = async (chain, tokenId) => {
    const { nft } = requireLatestContract(chain, 'NFT');
    return await nft.ownerOf(tokenId);
};
export const getETHBalance = async (chain, address) => {
    if (!address || !providers[chain] || !providers[chain].rpcProvider)
        return BigInt(0);
    try {
        return await providers[chain].rpcProvider.getBalance(address);
    }
    catch (e) {
        return BigInt(0);
    }
};
export const getMyETHBalance = async (chain) => {
    return await getETHBalance(chain, getMyProviderAddress(chain));
};
export const setRunnerAddress = (chain, wallet_address) => {
    const rpcProvider = providers[chain].rpcProvider;
    if (!wallet_address) {
        providers[chain].provider = rpcProvider;
        providers[chain].address = wallet_address;
        return;
    }
    const signer = new ethers.JsonRpcSigner(rpcProvider, wallet_address);
    providers[chain].provider = signer;
    providers[chain].address = wallet_address;
    setWallet(chain, signer);
};
export const setMintPrice = async (chain, price) => {
    throw new Error(`setMintPrice is not supported; use scheduleMintPriceChange : ${chain}`);
};
export const getTransactionStatus = async (chain, hash) => {
    const receipt = await providers[chain].rpcProvider.getTransactionReceipt(hash);
    if (!receipt)
        return 'Pending';
    return receipt.status === 1 ? 'Success' : 'Failed';
};
export const getGlobalState = async (chain) => {
    const wetMintPrice = await getMintPrice(chain);
    const owner = await contracts[chain][contracts[chain].length - 1].nft.owner();
    return {
        mintPrice: weiToEther(wetMintPrice),
        weiMintPrice: wetMintPrice,
        owner,
    };
};
